import { TileRadioGroup, type TileRadioGroupProps } from "@ui/components/TileRadioGroup/TileRadioGroup";
import { WizardTextArea } from "@ui/components/WizardTextArea";
import { accentSelectedText } from "@ui/lib/accent-classes";
import { tv } from "@ui/lib/tv";
import { type ReactNode, useRef } from "react";
import { useFollowUpAutoFocus } from "./useFollowUpAutoFocus";

export const radioGroupWithFollowUp = tv({
  slots: {
    wrapper: "w-full transition-all duration-300 ease-out",
    cardPanel: "grid transition-all duration-300 ease-out",
    inlineWrapper: "ml-4 flex flex-1 items-center gap-2",
    inlineLabel: "whitespace-nowrap font-normal",
    inlineInput: [
      "min-w-0 flex-1 bg-transparent",
      "font-normal text-neutral-800 text-xl leading-6 placeholder:text-neutral-500",
      "focus:outline-none",
    ],
  },
  variants: {
    hasCard: {
      true: {
        wrapper: "rounded-3xl bg-white p-6 shadow-[0_1px_0_0_rgba(0,0,0,0.16),0_0_0_1px_rgba(0,0,0,0.12)]",
      },
      false: { wrapper: "bg-transparent p-0 shadow-none" },
    },
    panelVisible: {
      true: { cardPanel: "mt-6 grid-rows-[1fr] opacity-100" },
      false: { cardPanel: "mt-0 grid-rows-[0fr] opacity-0" },
    },
    color: {
      red: { inlineLabel: accentSelectedText.red },
      green: { inlineLabel: accentSelectedText.green },
      purple: { inlineLabel: accentSelectedText.purple },
      yellow: { inlineLabel: accentSelectedText.yellow },
    },
  },
  defaultVariants: {
    hasCard: false,
    panelVisible: false,
    color: "red",
  },
});

export type RadioGroupFollowUpPresentation = "card" | "inline";

type CardFollowUp = {
  presentation: "card";
  /** When the selected value matches this, the follow-up panel opens. */
  triggerValue: string;
  /** Accessible label for the textarea. Hidden visually. */
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
};

type InlineFollowUp = {
  presentation: "inline";
  /** When the selected value matches this, the trigger item shows the inline input
   *  in place of its label. */
  triggerValue: string;
  /** Accessible label for the inline input. Hidden visually (or rendered as a
   *  small prefix when `prefix` is set). */
  label: string;
  /** Optional short prefix rendered before the input (e.g. "Yes:"). */
  prefix?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
};

type ContentFollowUp = {
  presentation: "content";
  /** When the selected value matches this, the panel opens with `content`. */
  triggerValue: string;
  /** Optional accessible label for the revealed region. */
  label?: string;
  /** Arbitrary follow-up UI — a money field, an upload field, a url input, etc. */
  content: ReactNode;
};

export type RadioGroupFollowUpConfig = CardFollowUp | InlineFollowUp | ContentFollowUp;

export type RadioGroupWithFollowUpItem = {
  value: string;
  label: ReactNode;
  isDisabled?: boolean;
};

export interface RadioGroupWithFollowUpProps extends Omit<TileRadioGroupProps, "children" | "className"> {
  items: RadioGroupWithFollowUpItem[];
  /** A single follow-up, or several keyed by their `triggerValue` (the matching one opens). */
  followUp: RadioGroupFollowUpConfig | RadioGroupFollowUpConfig[];
  className?: string;
}

/**
 * A `TileRadioGroup` paired with a follow-up that opens when the user picks a config's
 * `triggerValue`. Pass one config, or an array (e.g. a different follow-up per option).
 * Three presentations:
 *
 *  - **card** — the radios + a borderless textarea share one outer white card with an
 *    animated expand/collapse (the card is the textarea's surface).
 *  - **content** — a bare animated reveal of arbitrary `content` (a money field, an upload
 *    field, a url input…). No card surface; the content brings its own chrome.
 *  - **inline** — the input replaces the trigger item's label and the trigger
 *    item flex-grows to make room.
 *
 *  Pointer interactions auto-focus the first input via `useFollowUpAutoFocus` so the
 *  user can start typing immediately after clicking; keyboard / screen reader
 *  users tab to the input naturally.
 */
export function RadioGroupWithFollowUp({
  items,
  followUp,
  color = "red",
  size = "5xl",
  align = "vertical",
  className,
  ...props
}: RadioGroupWithFollowUpProps) {
  const configs = Array.isArray(followUp) ? followUp : [followUp];
  const inlineConfig = configs.find((c): c is InlineFollowUp => c.presentation === "inline");
  const panelConfigs = configs.filter(
    (c): c is CardFollowUp | ContentFollowUp => c.presentation === "card" || c.presentation === "content",
  );
  const activePanel = panelConfigs.find((c) => c.triggerValue === props.value);
  const inlineVisible = inlineConfig != null && props.value === inlineConfig.triggerValue;

  // Retain the last-shown panel so its content stays mounted through the collapse animation when its
  // trigger is deselected (e.g. Paid → Free). Without this `activePanel` becomes undefined the instant
  // the value changes, the content unmounts, and only an empty box animates out. The retained content
  // is marked `inert` while hidden so it stays out of the tab order and the accessibility tree.
  const lastPanelRef = useRef(activePanel);
  if (activePanel) lastPanelRef.current = activePanel;
  const displayPanel = activePanel ?? lastPanelRef.current;

  const cardRef = useFollowUpAutoFocus({ isVisible: Boolean(activePanel) });
  const inlineRef = useFollowUpAutoFocus({ isVisible: inlineVisible });

  const styles = radioGroupWithFollowUp({
    // Only the textarea (`card`) gets the white card surface — it renders borderless and the
    // card IS its frame. `content` follow-ups bring their own chrome (money / upload / url
    // fields), so they reveal bare to avoid a field-inside-a-card double border.
    hasCard: activePanel?.presentation === "card",
    panelVisible: Boolean(activePanel),
    color,
  });

  if (inlineConfig) {
    return (
      <div className={styles.wrapper({ className })}>
        <TileRadioGroup {...props} color={color} size={size} align={align}>
          {items.map((item) =>
            item.value === inlineConfig.triggerValue ? (
              <InlineTriggerItem
                key={item.value}
                followUp={inlineConfig}
                inputRef={inlineRef as React.RefObject<HTMLInputElement | null>}
                item={item}
                styles={styles}
              />
            ) : (
              <TileRadioGroup.Item key={item.value} isDisabled={item.isDisabled} value={item.value}>
                {item.label}
              </TileRadioGroup.Item>
            ),
          )}
        </TileRadioGroup>
      </div>
    );
  }

  // Card / content panel presentation
  return (
    <div className={styles.wrapper({ className })}>
      <TileRadioGroup {...props} align={align} color={color} size={size}>
        {items.map((item) => (
          <TileRadioGroup.Item key={item.value} isDisabled={item.isDisabled} value={item.value}>
            {item.label}
          </TileRadioGroup.Item>
        ))}
      </TileRadioGroup>

      <div className={styles.cardPanel()}>
        {/* The grid-row collapse needs vertical overflow clipping, but any non-visible overflow also
            clips a child's box-shadow — and a follow-up field's focus/validation halo is an 8px shadow
            that extends past the field on every side. So clip the y-axis (for the collapse) but inflate
            the clip box on both axes with matching negative-margin + padding (-mx-3/-my-3 + px-3/py-3,
            each 12px > the 8px halo): the content keeps its exact position while the halo paints in full,
            whether the field sits flush at the panel's top, bottom, or edges. */}
        <div
          ref={cardRef as React.RefObject<HTMLDivElement>}
          className="-mx-3 -my-3 overflow-y-hidden px-3 py-3"
          inert={activePanel ? undefined : true}
        >
          {displayPanel?.presentation === "card" ? (
            <WizardTextArea
              color={color}
              isLabelHidden
              label={displayPanel.label}
              onChange={displayPanel.onChange}
              placeholder={displayPanel.placeholder}
              rows={displayPanel.rows ?? 4}
              value={displayPanel.value}
              variant="plain"
            />
          ) : displayPanel?.presentation === "content" ? (
            displayPanel.content
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface InlineTriggerItemProps {
  item: RadioGroupWithFollowUpItem;
  followUp: InlineFollowUp;
  inputRef: React.RefObject<HTMLInputElement | null>;
  styles: ReturnType<typeof radioGroupWithFollowUp>;
}

/**
 * The trigger radio item is a regular `TileRadioGroup.Item` whose label slot
 * is overridden: when selected, the item flex-grows (`md:flex-[3]`) and swaps
 * its label for the inline input.
 */
function InlineTriggerItem({ item, followUp, inputRef, styles }: InlineTriggerItemProps) {
  return (
    <TileRadioGroup.Item
      className="items-center gap-3 data-[selected]:md:flex-[3]"
      isDisabled={item.isDisabled}
      value={item.value}
      renderLabel={({ isSelected }, labelClassName) =>
        isSelected ? (
          <div className={styles.inlineWrapper()}>
            {followUp.prefix ? <span className={styles.inlineLabel()}>{followUp.prefix}</span> : null}
            <input
              ref={inputRef}
              aria-label={followUp.label}
              className={styles.inlineInput()}
              onChange={(e) => followUp.onChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder={followUp.placeholder}
              type="text"
              value={followUp.value}
            />
          </div>
        ) : (
          <span className={labelClassName}>{item.label}</span>
        )
      }
    />
  );
}
